import * as t from './types'
import {
  Organization,
  foldEitherToAction,
  memoed,
  Loading,
  NotRequested,
  loadable,
} from '@library/react-toolkit'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import { pipe } from 'fp-ts/lib/function'
import { CancelToken } from 'axios'
import { takeLatest, matchPathGuard, waitForOrganization } from '../../saga'
import { task, either, array, option } from 'fp-ts'
import { LocationChangeAction } from 'connected-react-router'
import { lens } from 'lens.ts'
import { State } from '../../store'

export const initialState: t.State = {
  query: { page: 0, itemsPerPage: 10 },
  payers: memoed.init({ data: [], count: 0 }),
  addResult: NotRequested,
  deleteResult: NotRequested,
  createModalOpen: false,
  selectedPayer: option.none,
  editResult: NotRequested,
  editingPayer: option.none,
}

const _l = lens<t.State>()

export const reducer = (s: t.State = initialState, a: t.Actions): t.State => {
  switch (a.type) {
    case 'PAYERS/get':
      return pipe(
        s,
        _l.query.set(q => ({ ...q, ...a.payload })),
        _l.payers.set(memoed.step<t.CustomersData>(Loading))
      )
    case 'PAYERS/get_result':
      return pipe(s, _l.payers.set(memoed.step(a.payload)))
    case 'PAYERS/add':
      return pipe(s, _l.addResult.set(Loading))
    case 'PAYERS/add_result':
      return pipe(
        s,
        _l.addResult.set(a.payload),
        _l.createModalOpen.set(!loadable.isOk(a.payload))
      )
    case 'PAYERS/toggle_create_modal':
      return pipe(
        s,
        _l.createModalOpen.set(v => !v)
      )
    case 'PAYERS/delete':
      return pipe(s, _l.deleteResult.set(Loading))
    case 'PAYERS/delete_result':
      return pipe(s, _l.deleteResult.set(a.payload))
    case 'PAYERS/view':
      return pipe(s, _l.selectedPayer.set(option.some(a.payload)))
    case 'PAYERS/close_view':
      return pipe(s, _l.selectedPayer.set(option.none))
    case 'PAYERS/start_edit':
      return pipe(s, _l.editingPayer.set(option.some(a.payload)))
    case 'PAYERS/cancel_edit':
      return pipe(s, _l.editingPayer.set(option.none))
    case 'PAYERS/edit':
      return pipe(s, _l.editResult.set(Loading))
    case 'PAYERS/edit_result':
      return pipe(
        s,
        _l.editResult.set(a.payload),
        _l.editingPayer.set(c => (loadable.isOk(a.payload) ? option.none : c))
      )
    default:
      return s
  }
}

function* getPayersSaga(_: t.GetPayersAction, token?: CancelToken) {
  const q: t.State['query'] = yield effects.select((s: State) => s.payers.query)
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.getPayers(organization.name, q, token),
    task.map(foldEitherToAction(t.getPayersResult)),
    task.map(a => effects.put(a)),
    e => effects.call(e)
  )
}

function* addPayerSaga(a: t.AddPayerAction, token?: CancelToken) {
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.addPayer(organization.name, a.payload, token),
    task.map(
      either.fold(
        e => [t.addPayerResult.err(e)],
        r => [t.addPayerResult.ok(r), t.getPayersAction({})]
      )
    ),
    task.map(array.map(action => effects.put(action))),
    task.map(es => effects.all(es)),
    e => effects.call(e)
  )
}

function* deletePayerSaga(a: t.DeletePayerAction, token?: CancelToken) {
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.deletePayer(organization.name, a.payload.id, token),
    task.map(
      either.fold(
        e => [t.deletePayerResult.err(e)],
        r => [t.deletePayerResult.ok(r), t.getPayersAction({})]
      )
    ),
    task.map(array.map(action => effects.put(action))),
    task.map(es => effects.all(es)),
    e => effects.call(e)
  )
}

function* editPayerSaga(a: t.EditPayerAction, token?: CancelToken) {
  const editing: t.State['editingPayer'] = yield effects.select(
    (s: State) => s.payers.editingPayer
  )

  if (option.isNone(editing)) {
    return
  }

  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    api.editPayer(
      organization.name,
      { id: editing.value.id, ...a.payload },
      token
    ),
    task.map(
      either.fold(
        e => [t.editPayerResult.err(e)],
        r => [t.editPayerResult.ok(r), t.getPayersAction({})]
      )
    ),
    task.map(array.map(action => effects.put(action))),
    task.map(es => effects.all(es)),
    e => effects.call(e)
  )
}

function* changePageSaga(_: LocationChangeAction) {
  yield waitForOrganization()

  yield effects.put(t.getPayersAction({}))
}

export function* saga() {
  yield effects.all([
    takeLatest('PAYERS/edit', editPayerSaga),
    takeLatest('PAYERS/add', addPayerSaga),
    takeLatest('PAYERS/get', getPayersSaga),
    takeLatest('PAYERS/delete', deletePayerSaga),
    takeLatest(matchPathGuard('/bills/payers'), changePageSaga),
  ])
}
